import React, {useRef} from 'react';
import clsx from 'clsx';
import type {FixedSizeList} from 'react-window';
import type {SetOptional} from 'type-fest';
import {faCheck} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
	Autocomplete,
	autocompleteClasses,
	type AutocompleteProps,
	type AutocompleteRenderInputParams,
	type AutocompleteValue,
	ListItem,
	ListItemIcon,
	ListItemText,
	ListSubheader,
	Popper,
} from '@mui/material';
import {inputBaseClasses} from '@mui/material/InputBase';
import {inputLabelClasses} from '@mui/material/InputLabel';
import {ChipSkeleton} from '@netx/core-components/Chip';
import {ErrorField} from '@netx/core-components/ErrorField';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {
	composeClasses,
	styled,
	type WithClasses,
} from '@netx/core-components/Theme';
import {splitEventHandler} from '@netx/core-next/utils/fn';
import {combineRefs} from '@netx/core-next/utils/react';
import {i18n} from '@netx/core/i18n';
import ListBox from './components/ListBox';
import {
	SelectFieldInput,
	type VirtualizedInputProps,
} from './components/SelectFieldInput';
import ValueChip from './components/ValueChip';
import {SelectLabels} from './locales';
import selectFieldClasses, {
	getSelectFieldUtilityClass,
	type SelectFieldClasses,
} from './selectFieldClasses';

export type {FilterOptionsState} from '@mui/base/useAutocomplete';

export type SelectFieldElement = HTMLDivElement;
export type SelectFieldRef = React.ForwardedRef<SelectFieldElement>;

const valueNotEmpty = (value: any) => value !== '' && value != null;

const useUtilityClasses = (ownerState: any) => {
	const {classes, multiple} = ownerState;
	const slots = {
		root: ['root', multiple && 'multiple'],
		autocomplete: ['autocomplete', multiple && 'multiple'],
		selectedIcon: ['selectedIcon'],
	};
	return composeClasses(
		slots,
		getSelectFieldUtilityClass,
		classes as typeof selectFieldClasses,
	);
};

const SelectFieldRoot = styled('div', {
	name: 'SelectField',
	slot: 'Root',
	overridesResolver: (props, styles) => styles.root,
})(({theme}) => ({
	display: 'inline-flex',
	flexDirection: 'column',
	minWidth: 0,
	width: '100%',
	// TODO: this is related to our size override for fa icons in theme
	// need to better handle this issue as using `important` is code smell
	// at this stage
	'& .MuiAutocomplete-clearIndicator .MuiSvgIcon-root': {
		minWidth: `8px !important`,
		minHeight: `8px !important`,
	},
	'& .MuiFormHelperText-root': {
		whiteSpace: 'pre-wrap',
		wordBreak: 'break-all',
	},
	[`&.${selectFieldClasses.multiple}`]: {
		// Make the input area minimum size match the size it would be if it had a value
		// otherwise we wind up with the interface jumping slightly betweem value and no
		// value
		[`& .${inputLabelClasses.root}`]: {
			lineHeight: '1rem',
		},
		[`& .${inputBaseClasses.root}`]: {
			minHeight: 50,
			[`& .${inputBaseClasses.input}`]: {
				minHeight: 28,
			},
		},
		[`& .${inputLabelClasses.outlined}:not(.${inputLabelClasses.shrink})`]: {
			transform: 'translate(14px, 18px) scale(1)',
		},
	},
}));

const ListItemIconRoot = styled(FontAwesomeIcon, {
	name: 'SelectField',
	slot: 'SelectedIcon',
	overridesResolver: (props, styles) => styles.selectedIcon,
})(({theme}) => ({
	color: theme.palette.secondary.main,
}));

const AutocompleteRoot = styled(Autocomplete, {
	name: 'SelectField',
	slot: 'Autocomplete',
	overridesResolver: (props, styles) => styles.autocomplete,
})(({theme}) => ({
	width: '100%',
})) as <
	T,
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
	FreeSolo extends boolean = false,
>(
	props: AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
) => JSX.Element;

const PopperRoot = styled(Popper)(({theme}) => ({
	[`& .${autocompleteClasses.loading}, & .${autocompleteClasses.noOptions}`]: {
		color: theme.palette.text.primary,
	},
}));

export type SelectFieldProps<
	T,
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
	FreeSolo extends boolean = false,
> = SetOptional<
	Omit<
		AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
		'classes' | 'multiple'
	>,
	'renderInput'
> &
	WithClasses<SelectFieldClasses> & {
		error?: string;
		getListOptionLabel?: (value: T) => string;
		hasSecondaryText?: boolean;
		hideErrorMessage?: boolean;
		helperText?: React.ReactNode;
		inputRef?: React.Ref<HTMLInputElement>;
		label?: string | React.ReactNode;
		loading?: boolean;
		loadingValue?: boolean;
		loadingOptions?: boolean;
		multiple?: Multiple;
		name?: string;
		notForValueDisplay?: boolean;
		placeholder?: string;
		selectedIconOnLeft?: boolean;
		hideSelectedIcon?: boolean;
		required?: boolean;
		variant?: VirtualizedInputProps['variant'];
		getSelectedOptionLabel?: (value?: T) => string;
		getOptionSecondaryLabel?: (value: T) => string;
		onSearch?: (value: string) => void;
		InputProps?: Partial<
			Omit<VirtualizedInputProps, 'classes' | 'options' | 'onChange' | 'value'>
		>;
	};

const getSafeValues = <
	T,
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
	FreeSolo extends boolean = false,
>(
	values?: AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>,
) => (values && Array.isArray(values) ? values : []);

const renderGroup = (params: any) => [
	params.group !== '' ? (
		<ListSubheader key={params.key} component="div">
			{params.group}
		</ListSubheader>
	) : null,
	params.children,
];

const SelectFieldInner = <
	T,
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
	FreeSolo extends boolean = false,
>(
	{
		error,
		inputRef: _inputRef,
		label,
		loadingOptions,
		hideSelectedIcon,
		name,
		notForValueDisplay,
		required,
		selectedIconOnLeft = false,
		variant,
		InputProps,
		readOnly,
		...props
	}: SelectFieldProps<T, Multiple, DisableClearable, FreeSolo>,
	ref: SelectFieldRef,
) => {
	const {
		classes: _classes,
		className,
		disabled,
		hasSecondaryText,
		helperText,
		hideErrorMessage,
		loading,
		loadingValue,
		multiple,
		options,
		placeholder,
		sx,
		value,
		getListOptionLabel,
		getOptionLabel: _getOptionLabel,
		getOptionSecondaryLabel,
		getSelectedOptionLabel,
		onChange,
		onSearch,
		...sendProps
	} = props;

	const classes = useUtilityClasses({
		classes: _classes,
		multiple,
	});

	const inputRef = useRef<HTMLInputElement>(null);

	const fixedSizeListRef = useRef<FixedSizeList>();

	const inputValueChangesSinceSelection = useRef(true);

	type _AutoCompleteProps = Required<
		AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>
	>;

	const getOptionLabel = useConstCallback<_AutoCompleteProps['getOptionLabel']>(
		(option) => {
			if (loading) return i18n.t('i18n.loading');
			return _getOptionLabel ? _getOptionLabel(option) : (option as string);
		},
	);

	const handleChange = useConstCallback<_AutoCompleteProps['onChange']>(
		(event, value, reason, details) => {
			inputValueChangesSinceSelection.current = false;
			if (!event) return;
			if (Array.isArray(value))
				value = value.filter(valueNotEmpty) as AutocompleteValue<
					T,
					Multiple,
					DisableClearable,
					FreeSolo
				>;
			onChange?.(event, value, reason, details);
		},
	);

	const renderInput = useConstCallback(
		(params: AutocompleteRenderInputParams) => {
			return (
				<SelectFieldInput
					{...params}
					{...InputProps}
					error={error}
					helperText={helperText}
					inputRef={combineRefs(inputRef, _inputRef, params.inputProps.ref)}
					loading={loading || loadingValue || loadingOptions}
					label={label}
					name={name}
					readOnly={readOnly}
					placeholder={placeholder}
					required={required}
					variant={variant}
					inputProps={{
						...params.inputProps,
						...InputProps?.inputProps,
						// hackity hack hack hack
						// value: notForValueDisplay ? inputValueChangesSinceSelection.current ? (props.inputValue ?? '') : '' : (InputProps?.inputProps?.value ?? params.inputProps.value),
						value: notForValueDisplay
							? props.inputValue ?? ''
							: getSelectedOptionLabel
							? getSelectedOptionLabel(value as T)
							: InputProps?.inputProps?.value ?? params.inputProps.value,
					}}
					InputProps={{
						...params.InputProps,
						...InputProps?.InputProps,
					}}
					InputLabelProps={{
						...params?.InputLabelProps,
						...InputProps?.InputLabelProps,
					}}
				/>
			);
		},
	);

	const renderOption = useConstCallback<_AutoCompleteProps['renderOption']>(
		(props, option, state) => (
			<ListItem {...props}>
				{!hideSelectedIcon && selectedIconOnLeft && (
					<ListItemIcon style={{minWidth: 36}}>
						{state.selected && (
							<ListItemIconRoot
								style={{width: 16}}
								className={classes.selectedIcon}
								icon={faCheck}
							/>
						)}
					</ListItemIcon>
				)}

				<ListItemText
					primary={(getListOptionLabel || getOptionLabel)(option)}
					primaryTypographyProps={{
						noWrap: true,
					}}
					secondary={getOptionSecondaryLabel?.(option)}
					secondaryTypographyProps={{
						noWrap: true,
					}}
				/>
				{!hideSelectedIcon && !selectedIconOnLeft && state.selected && (
					<ListItemIconRoot
						style={{width: 16, marginLeft: 8}}
						className={classes.selectedIcon}
						icon={faCheck}
					/>
				)}
			</ListItem>
		),
	);

	const renderTags = useConstCallback<_AutoCompleteProps['renderTags']>(
		(values, getTagProps) =>
			loadingValue ? (
				<ChipSkeleton count={3} />
			) : (
				getSafeValues(values as any).map((value: any, index: number) => {
					return (
						<ValueChip
							{...getTagProps({index})}
							disabled={disabled}
							onSearch={onSearch}
							value={getOptionLabel(value)}
						/>
					);
				})
			),
	);

	return (
		<SelectFieldRoot className={clsx(classes.root, className)} sx={sx}>
			<AutocompleteRoot<T, Multiple, DisableClearable, FreeSolo>
				ref={ref}
				autoHighlight
				blurOnSelect={!multiple && !notForValueDisplay}
				clearOnBlur
				clearText={SelectLabels.CLEAR}
				// auto select input text when field is focused
				// for easier filtering/value replacement
				// non multiselect only
				selectOnFocus={!multiple && !notForValueDisplay}
				disableCloseOnSelect={multiple || notForValueDisplay}
				// We handle this in the `ListBox`
				handleHomeEndKeys={false}
				multiple={multiple}
				noOptionsText={i18n.t('i18n.noMatches')}
				openOnFocus
				renderGroup={renderGroup}
				renderInput={renderInput}
				renderOption={renderOption}
				renderTags={renderTags}
				{...sendProps}
				className={clsx(classes.autocomplete)}
				getOptionLabel={getOptionLabel}
				onInputChange={splitEventHandler(
					sendProps.onInputChange,
					(event, value, reason) => {
						inputValueChangesSinceSelection.current = true;
					},
				)}
				onChange={handleChange}
				disabled={disabled || loadingValue}
				readOnly={readOnly}
				id={props.id}
				loading={loading || loadingValue}
				// This is a hack to show loading state when values are being "loaded"
				// the autocomplete will not run `renderTags` if there is no value
				options={options || []}
				value={
					notForValueDisplay
						? null
						: loadingValue && multiple
						? ([''] as any)
						: value
				}
				ListboxComponent={ListBox as any}
				PopperComponent={PopperRoot}
				ListboxProps={{
					...sendProps?.ListboxProps,
					// TODO: the typing changed here in v5-alpha
					// there is no way (that i know of atm) to get
					// it to recognize the `props` of `ListBox` as
					// valid `props` to pass to `ListboxComponent`
					// https://github.com/mui-org/material-ui/issues/26342
					// @ts-ignore
					fixedSizeListRef,
					hasSecondaryText,
				}}
			/>
			{!hideErrorMessage ? (
				<ErrorField disabled={disabled} error={error} />
			) : null}
		</SelectFieldRoot>
	);
};

export const SelectFieldPrimitive = React.forwardRef(SelectFieldInner) as <
	T,
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
	FreeSolo extends boolean = false,
>(
	props: SelectFieldProps<T, Multiple, DisableClearable, FreeSolo>,
) => React.ReactElement;

export const SelectField = SelectFieldPrimitive;

export default SelectField;
